import { Code, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import { CommonTooltip } from 'components/common/tooltip';
import {
  ArchiveModelsDialog,
  CopyModelDialog,
  MachineModelCreationDialog,
  MachineModelEditorDialog,
  ModelPerformanceDialog,
} from 'components/sections/admin-portal/machine-models/dialogs';
import { MachineModelsToolbar } from 'components/sections/admin-portal/machine-models/toolbar';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { MachineModelsContext } from 'contexts/admin/machine-models.context';
import lightFormat from 'date-fns/lightFormat';
import parseISO from 'date-fns/parseISO';
import { ACTIONS_KEY, TABLES } from 'enums/tables';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import {
  MachineModelsDialog,
  MachineModelsFilters,
} from 'interfaces/i-machine-models-table';
import { IMenuAction } from 'interfaces/i-menus';
import { ITableCheckable } from 'interfaces/tables/checking';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSortable } from 'interfaces/tables/sorting';
import { getModelKeyFromModel } from 'lib_ts/classes/ms.helper';
import { LOCAL_DATE_FORMAT } from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IMachineModel } from 'lib_ts/interfaces/modelling/i-machine-model';
import { first } from 'lodash';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';
import useMiscStore from 'stores/use-misc-store';

const ALLOW_DELETE = false;
const PAGE_SIZES = TABLES.PAGE_SIZES.MD;

export const MachineModelsTable = () => {
  const anyOpenDialogs = useMiscStore((s) => s.anyOpenDialogs);

  const [filters, setFilters] = useState<MachineModelsFilters>({
    statuses: [],
    ballTypes: [],
    types: [],
    machines: [],
    names: [],
    created: [],
  });

  const [dialog, setDialog] = useState<{
    mode: MachineModelsDialog;
    key: number;
    models: Nullable<IMachineModel[]>;
  }>();

  const modelsCx = useContext(MachineModelsContext);

  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const machines = useMachinesPortalStore((s) => s.machines);

  const closeDialog = () => {
    setDialog(undefined);
  };

  const columns = useMemo<ITypedTableColumn<IMachineModel>[]>(
    () => [
      {
        label: 'common.actions',
        key: ACTIONS_KEY,
        actions: [
          {
            label: 'common.edit',
            onClick: (model) => {
              setDialog({
                mode: MachineModelsDialog.DialogEditor,
                key: Date.now(),
                models: [model],
              });
            },
          },
          {
            label: 'admin.create-from',
            onClick: (model) => {
              setDialog({
                mode: MachineModelsDialog.DialogCreate,
                key: Date.now(),
                models: [model],
              });
            },
          },
          {
            label: 'admin.compare-w-active',
            onClick: (model) => {
              const machine = machines.find(
                (m) => m.machineID === model.machineID
              );
              const modelKey = getModelKeyFromModel(model);
              const currentModelID = machine?.model_ids?.[modelKey];

              const active = modelsCx.models.find(
                (m) => m._id === currentModelID
              );
              if (!active) {
                NotifyHelper.error({
                  message_md: `Failed to find the model for ${model.machineID} associated with model key ${modelKey}, nothing to compare.`,
                });
                return;
              }

              setDialog({
                mode: MachineModelsDialog.DialogPerformance,
                key: Date.now(),
                models: [model, active],
              });
            },
          },
          {
            label: 'admin.view-performance',
            onClick: (model) => {
              setDialog({
                mode: MachineModelsDialog.DialogPerformance,
                key: Date.now(),
                models: [model],
              });
            },
          },
          {
            label: 'common.copy',
            onClick: (model) => {
              setDialog({
                mode: MachineModelsDialog.DialogCopy,
                key: Date.now(),
                models: [model],
              });
            },
          },
          {
            label: 'common.archive',
            onClick: (model) => {
              setDialog({
                mode: MachineModelsDialog.DialogArchive,
                key: Date.now(),
                models: [model],
              });
            },
            color: RADIX.COLOR.DANGER,
            invisibleFn: () => ALLOW_DELETE,
          },
        ],
      },
      {
        label: 'common.created',
        key: '_created',
        formatFn: (m) =>
          m._created
            ? lightFormat(parseISO(m._created), LOCAL_DATE_FORMAT)
            : '',
      },
      {
        label: 'common.name',
        key: 'name',
      },
      {
        label: 'common.type',
        key: 'type',
      },
      {
        label: 'common.machine',
        key: 'machineID',
      },
      {
        label: 'admin.status',
        key: 'status',
      },
      {
        label: 'common.ball-type',
        key: 'ball_type',
      },
      {
        label: 'Investigation Notes',
        key: 'investigation_notes',
        formatFn: (m) => {
          return (
            <CommonTooltip
              trigger={<Text truncate>{m.investigation_notes}</Text>}
              text={m.investigation_notes}
            />
          );
        },
      },
      {
        label: 'common.tags',
        key: '_tags',
        disableSort: true,
        invisible: true,
        formatFn: (m) => {
          if (!m.tags) {
            return null;
          }

          return (
            <>
              {m.tags.map((t, i) => (
                <Code key={`model-${m._id}-tag-${i}`} className="block">
                  {t}
                </Code>
              ))}
            </>
          );
        },
      },
    ],
    [machines, modelsCx.models]
  );

  const updateFilters = (filter: Partial<MachineModelsFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...filter,
    }));
  };

  const filtered = useMemo(() => {
    return modelsCx.models
      .filter(
        (m) => filters.names.length === 0 || filters.names.includes(m.name)
      )
      .filter(
        (m) =>
          filters.machines.length === 0 ||
          filters.machines.includes(m.machineID)
      )
      .filter(
        (m) =>
          filters.statuses.length === 0 ||
          (m.status && filters.statuses.includes(m.status))
      )
      .filter(
        (m) =>
          filters.ballTypes.length === 0 ||
          (m.ball_type && filters.ballTypes.includes(m.ball_type))
      )
      .filter(
        (m) => filters.types.length === 0 || filters.types.includes(m.type)
      )
      .filter((m) => {
        if (filters.hasInvestigationNotes === undefined) {
          return true;
        }

        return !filters.hasInvestigationNotes === !m.investigation_notes;
      })
      .filter(
        (m) =>
          filters.created.length === 0 ||
          (m._created &&
            filters.created.includes(
              lightFormat(parseISO(m._created), LOCAL_DATE_FORMAT)
            ))
      );
  }, [modelsCx.models, filters]);

  const getCheckedMenuActions = useCallback(
    (checked: IMachineModel[]): IMenuAction[] => [
      {
        label: 'Compare checked',
        onClick: () => {
          setDialog({
            mode: MachineModelsDialog.DialogPerformance,
            key: Date.now(),
            models: checked,
          });
        },
      },
      {
        label: 'Archive checked',
        color: RADIX.COLOR.DANGER,
        onClick: () => {
          setDialog({
            mode: MachineModelsDialog.DialogArchive,
            key: Date.now(),
            models: checked,
          });
        },
      },
    ],
    []
  );

  const pagination: ITablePageable = {
    paginationID: PaginationID.AdminModelsList,
    total: filtered.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: '_created',
      dir: 1,
    },
  };

  const checkable: ITableCheckable = {
    checkboxColumnIndex: 0,
    checkedActions: getCheckedMenuActions,
  };

  const firstDialogModel = first(dialog?.models);

  return (
    <ErrorBoundary identifier="MachineModelsTable">
      <FlexTableWrapper
        gap={RADIX.FLEX.GAP.SECTION}
        table={
          <CommonTableHoC
            dataTestID="AdminMachineModels"
            identifier="AdminMachineModelsTable"
            toolbarContent={
              <MachineModelsToolbar
                filters={filters}
                updateFilters={updateFilters}
                openDialog={(m) => {
                  // mainly to open the create dialog
                  setDialog({
                    mode: m,
                    key: Date.now(),
                    models: null,
                  });
                }}
              />
            }
            displayColumns={columns}
            displayData={filtered}
            enableListener={!anyOpenDialogs}
            {...pagination}
            {...checkable}
            {...sort}
            vFlex
          />
        }
      />

      {dialog?.mode === MachineModelsDialog.DialogEditor &&
        firstDialogModel?._id && (
          <MachineModelEditorDialog
            key={dialog.key}
            id={firstDialogModel._id}
            userRoleFeatureFlags={userRoleFeatureFlags}
            machineModelsCx={modelsCx}
            onClose={closeDialog}
          />
        )}

      {dialog?.mode === MachineModelsDialog.DialogCopy && firstDialogModel && (
        <CopyModelDialog
          key={dialog.key}
          model={firstDialogModel}
          onClose={closeDialog}
        />
      )}

      {dialog?.mode === MachineModelsDialog.DialogCreate && (
        <MachineModelCreationDialog
          key={dialog.key}
          refModel={firstDialogModel}
          onClose={(refresh) => {
            if (refresh) {
              modelsCx.refresh();
            }
            closeDialog();
          }}
        />
      )}

      {dialog?.mode === MachineModelsDialog.DialogPerformance &&
        dialog.models &&
        dialog.models.length > 0 && (
          <ModelPerformanceDialog
            key={dialog.key}
            models={dialog.models}
            onClose={closeDialog}
          />
        )}

      {dialog?.mode === MachineModelsDialog.DialogArchive &&
        dialog.models &&
        dialog.models.length > 0 && (
          <ArchiveModelsDialog
            key={dialog.key}
            models={dialog.models}
            onClose={closeDialog}
          />
        )}
    </ErrorBoundary>
  );
};
